import React, { useState } from 'react';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  TextField,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://mobilemoneystatementanalyzer.com';
const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';
const SUPPORT_EMAIL = 'statementanalyzer@mobilemoneystatementanalyzer.com';
const EMAIL_SUBJECT_PLAY = 'Account deletion — M2 Stats';

export default function DeleteAccountPage() {
  const { user, loading, getAuthHeaders, logout } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const mailtoPlay = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(EMAIL_SUBJECT_PLAY)}`;

  const handleDelete = async () => {
    setError('');
    if (confirmText.trim() !== CONFIRM_PHRASE) {
      setError(`Type exactly: ${CONFIRM_PHRASE}`);
      return;
    }
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/account/delete`,
        { confirmation: CONFIRM_PHRASE },
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      setDone(true);
      await logout();
      window.location.href = '/';
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === 'object' && d !== null) {
        const w = Array.isArray(d.warnings) ? d.warnings.join('; ') : '';
        setError([d.message || 'Deletion failed', w].filter(Boolean).join(' — '));
      } else if (typeof d === 'string') {
        setError(d);
      } else {
        setError(err.message || 'Could not delete account. Try again or contact support.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Button component="a" href="/" startIcon={<ArrowBackIcon />} color="inherit" sx={{ mr: 2 }}>
            Home
          </Button>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Account &amp; data deletion
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {loading && (
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Checking whether you are signed in…
            </Typography>
          </Box>
        )}

        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Delete your M2 Stats / Mobile Money Statement Analyzer account
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          M2 Stats — account and data deletion
        </Typography>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 0 }}>
            <strong>Developer / company:</strong> Winflamestech Solutions
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 0 }}>
            This page explains how to <strong>request deletion</strong> of your <strong>account</strong> and{' '}
            <strong>associated data</strong> for <strong>M2 Stats</strong> (Android app) and the{' '}
            <strong>Mobile Money Statement Analyzer</strong> website when you use a <strong>registered account</strong>{' '}
            (email/password or Google sign-in).
          </Typography>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            How to request account deletion
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose <strong>one</strong> of the following:
          </Typography>

          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Option A — Email (works for everyone)
          </Typography>
          <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 3 }}>
            <ListItem>
              <ListItemText
                primary="1. Send from your account email"
                secondary="Use the same address linked to your account. If you cannot access that inbox, say so in your message."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="2. To"
                secondary={
                  <Box component="a" href={mailtoPlay} sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                    {SUPPORT_EMAIL}
                  </Box>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="3. Subject"
                secondary={<Box component="span" sx={{ fontFamily: 'monospace' }}>{EMAIL_SUBJECT_PLAY}</Box>}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="4. Body"
                secondary="State that you want your account and associated data deleted, and include the email address your account uses (if different from the sender)."
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" paragraph>
            We will reply to confirm receipt and may ask a brief identity check to protect your account.
          </Typography>
          <Button variant="outlined" color="primary" component="a" href={mailtoPlay} sx={{ mb: 2 }}>
            Open email app
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={700} gutterBottom id="option-b">
            Option B — While signed in on the website
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If you are logged in on{' '}
            <Box component="a" href={SITE_URL} sx={{ fontWeight: 600 }}>
              mobilemoneystatementanalyzer.com
            </Box>
            , you may use the <strong>self-service deletion</strong> below on this page. Option A (email) remains
            available if you prefer.
          </Typography>
          <Button variant="text" component="a" href="#self-service" sx={{ fontWeight: 600 }}>
            Jump to self-service deletion
          </Button>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            What we delete when your request is completed
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            When we process a complete account deletion request, we aim to <strong>delete or anonymize</strong> data{' '}
            <strong>we hold in connection with your account</strong>, including for example:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Your user account with our authentication provider (e.g. sign-in identity)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Preferences stored for your account (such as custom entity or category labels you synced to the account)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Saved analyses or upload-related records stored in your account on our systems (e.g. dashboard data tied to that login), where applicable." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Subscriptions, payments, and usage records we hold for that account (where applicable)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Template purchases and other account-linked records in our app database." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Mobile app data synced to our servers under your login (where applicable)." />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              M2 Stats (mobile app)
            </Typography>
            <Typography variant="body2">
              Deleting your <strong>server-side account</strong> does <strong>not</strong> automatically erase data that
              exists <strong>only on your phone</strong> (for example, SMS read by the app into local views, or
              local-only entries and settings). You can <strong>clear app data</strong> or <strong>uninstall</strong> the
              app in Android settings to remove local information on the device.
            </Typography>
          </Alert>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            What we may keep (and for how long)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>No longer than necessary</strong> for the purposes below, and <strong>only</strong> where required or
            standard practice:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Short-term backups"
                secondary="Copies may persist for a limited time in encrypted backups, then age out."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Legal, security, and fraud prevention"
                secondary="We may retain minimal records if the law requires or to establish or defend a legal claim."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Aggregated or de-identified data"
                secondary="Data that does not identify you."
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
            We do <strong>not</strong> keep your full statement or account data for marketing after a confirmed account
            deletion, except as required by law or as stated above.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Typical completion time:</strong> we aim to <strong>complete</strong> account deletion (or to
            confirm any limitation) within <strong>30 calendar days</strong> of a verified request, unless a longer
            period is required by law or a technical constraint—we will inform you if so.
          </Typography>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Questions
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Email:</strong>{' '}
            <Box component="a" href={mailtoPlay} sx={{ fontWeight: 600 }}>
              {SUPPORT_EMAIL}
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            For general privacy information, see our{' '}
            <Box component="a" href={`${SITE_URL}/privacy`} sx={{ fontWeight: 600 }}>
              Privacy Policy
            </Box>
            .
          </Typography>
        </Paper>

        <Paper id="self-service" sx={{ p: 3, mb: 2, scrollMarginTop: 72 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Self-service on this website (Option B)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            When signed in, you can permanently delete this account and the data described above by typing the
            confirmation phrase. This cannot be undone.
          </Typography>

          {!loading && !user && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  You are not signed in. To use self-service deletion, sign in first, then return here or refresh this
                  page.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Button size="small" variant="contained" component="a" href="/login">
                    Sign in
                  </Button>
                  <Button size="small" variant="outlined" component="a" href="/">
                    Home
                  </Button>
                </Stack>
              </Alert>
              <Typography variant="subtitle2" gutterBottom>
                After you sign in
              </Typography>
              <Stack spacing={1.5} sx={{ mb: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  1. Use <strong>Sign in</strong> or open the home page, choose <strong>Dashboard</strong>, and sign in
                  with the same email (and password or Google) you used when you registered.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  2. Open this page again or refresh— the confirmation form will appear below.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  3. Type <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{CONFIRM_PHRASE}</Box>{' '}
                  and submit. You will be signed out automatically.
                </Typography>
              </Stack>
            </>
          )}

          {!loading && user && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Signed in as <strong>{user.email}</strong>.
              </Typography>
              {done && <Alert severity="success" sx={{ mb: 2 }}>Account removed. Redirecting…</Alert>}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                To confirm, type{' '}
                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {CONFIRM_PHRASE}
                </Box>{' '}
                below:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={submitting}
                placeholder={CONFIRM_PHRASE}
                sx={{ mb: 2 }}
                autoComplete="off"
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
                <Button variant="outlined" component="a" href="/" disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={submitting || confirmText.trim() !== CONFIRM_PHRASE}
                >
                  {submitting ? <CircularProgress size={22} color="inherit" /> : 'Delete my account'}
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
